'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { jobService } from '../../services/jobService';
import { Job } from '../../types/supabase.types';
import Image from 'next/image';
import Header from '@/components/common/Header';

export default function JobsPage() {
  const router = useRouter();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedExperience, setSelectedExperience] = useState<string>('all');
  const [selectedJobType, setSelectedJobType] = useState<string>('all');
  const [selectedLocation, setSelectedLocation] = useState<string>('all');

  useEffect(() => {
    loadJobs();
  }, [searchQuery, selectedExperience, selectedJobType, selectedLocation]);

  const loadJobs = async () => {
    setLoading(true);
    setError('');
    
    try {
      const { data, error } = await jobService.getAll({
        search: searchQuery,
        experience: selectedExperience,
        jobType: selectedJobType,
        location: selectedLocation,
      });

      if (error) {
        setError(error.message);
      } else {
        setJobs(data || []);
      }
    } catch (err) {
      setError('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleJobClick = (jobId: string) => {
    router.push(`/job-details-page?id=${jobId}`);
  };

  return (
    <div className="bg-[#101822] text-slate-100 font-sans antialiased overflow-x-hidden min-h-screen">
      <Header />
      {/* Main Content */}
      <main className="pt-32 pb-24 relative mesh-bg">
        <div className="absolute inset-0 grid-pattern pointer-events-none"></div>
        
        <section className="relative z-10 max-w-7xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#2b7cee]/10 text-[#2b7cee] border border-[#2b7cee]/20 mb-6">
              <span className="material-symbols-outlined text-base">work_outline</span>
              <span className="text-[10px] font-bold tracking-[0.2em] uppercase">Career Opportunities</span>
            </div>
            <h1 className="font-display text-5xl md:text-6xl font-extrabold tracking-tight mb-6">
              Product Marketing <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#FF6B4A] to-[#8B5CF6]">Jobs</span>
            </h1>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
              India's premier job board dedicated exclusively to PMM roles. Handpicked opportunities from high-growth startups and tech giants.
            </p>
          </div>

          {/* Search and Filters */}
          <div className="max-w-4xl mx-auto mb-16">
            <div className="glass-card p-4 rounded-[2rem] mb-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <span className="material-symbols-outlined absolute left-5 top-1/2 -translate-y-1/2 text-slate-500">search</span>
                  <input 
                    className="w-full bg-slate-900/50 border-white/5 rounded-2xl py-4 pl-14 pr-4 text-slate-200 focus:ring-[#2b7cee]/50 focus:border-[#2b7cee]/50 transition-all placeholder-slate-600" 
                    placeholder="Search roles, companies, or keywords..." 
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-3">
              <div className="relative group">
                <select 
                  className="appearance-none bg-slate-900/40 border border-white/10 rounded-xl px-5 py-2.5 text-sm text-slate-300 focus:ring-[#2b7cee] focus:border-[#2b7cee] min-w-[160px] cursor-pointer hover:bg-slate-800 transition-colors"
                  value={selectedExperience}
                  onChange={(e) => setSelectedExperience(e.target.value)}
                >
                  <option value="all">Experience Level</option>
                  <option value="0-2">0-2 Years</option>
                  <option value="3-5">3-5 Years</option>
                  <option value="6-10">6-10 Years</option>
                </select>
              </div>

              <div className="relative group">
                <select 
                  className="appearance-none bg-slate-900/40 border border-white/10 rounded-xl px-5 py-2.5 text-sm text-slate-300 focus:ring-[#2b7cee] focus:border-[#2b7cee] min-w-[160px] cursor-pointer hover:bg-slate-800 transition-colors"
                  value={selectedJobType}
                  onChange={(e) => setSelectedJobType(e.target.value)}
                >
                  <option value="all">Job Type</option>
                  <option value="Full-time">Full-time</option>
                  <option value="Contract">Contract</option>
                  <option value="Remote">Remote</option>
                </select>
              </div>

              <div className="relative group">
                <select 
                  className="appearance-none bg-slate-900/40 border border-white/10 rounded-xl px-5 py-2.5 text-sm text-slate-300 focus:ring-[#2b7cee] focus:border-[#2b7cee] min-w-[160px] cursor-pointer hover:bg-slate-800 transition-colors"
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                >
                  <option value="all">Location</option>
                  <option value="Bengaluru">Bengaluru</option>
                  <option value="Mumbai">Mumbai</option>
                  <option value="Gurgaon">Gurgaon</option>
                </select>
              </div>
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#2b7cee] border-t-transparent"></div>
              <p className="mt-4 text-slate-400">Loading jobs...</p>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="text-center py-12">
              <div className="glass-card p-6 max-w-md mx-auto rounded-2xl">
                <h3 className="text-xl font-bold text-white mb-2">Failed to Load Jobs</h3>
                <p className="text-slate-400 mb-4">{error}</p>
                <button
                  onClick={loadJobs}
                  className="px-6 py-2 bg-[#2b7cee] text-white rounded-lg hover:bg-[#2b7cee]/90 transition-colors font-medium"
                >
                  Try Again
                </button>
              </div>
            </div>
          )}

          {/* Job Cards Grid */}
          {!loading && !error && jobs?.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {jobs?.map((job) => (
                <div key={job?.id} className="glass-card p-8 rounded-[2.5rem] job-card group relative cursor-pointer" onClick={() => handleJobClick(job?.id)}>
                  <div className="flex items-start justify-between mb-8">
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center border-4 border-slate-900 shadow-xl overflow-hidden">
                      <span className="material-symbols-outlined text-slate-900 text-3xl">corporate_fare</span>
                    </div>
                    {job?.remote && (
                      <span className="px-3 py-1 bg-green-500/10 text-green-400 text-[10px] font-bold uppercase tracking-wider rounded-full border border-green-500/20">Remote Friendly</span>
                    )}
                  </div>

                  <div className="mb-8">
                    <h4 className="text-slate-300 font-extrabold text-sm mb-2 uppercase tracking-wide">{job?.company}</h4>
                    <h3 className="text-2xl font-bold leading-tight text-white group-hover:text-[#2b7cee] transition-colors">{job?.job_title}</h3>
                  </div>

                  <div className="space-y-4 mb-10">
                    <div className="flex items-center gap-3 text-slate-400">
                      <span className="material-symbols-outlined text-xl text-[#2b7cee]/70">location_on</span>
                      <span className="text-sm font-medium">{job?.location}</span>
                    </div>
                    <div className="flex items-center gap-3 text-slate-400">
                      <span className="material-symbols-outlined text-xl text-[#2b7cee]/70">work</span>
                      <span className="text-sm font-medium">{job?.years_of_experience} experience</span>
                    </div>
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleJobClick(job?.id);
                    }}
                    className="inline-flex items-center justify-between w-full px-6 py-3.5 border border-white/10 rounded-full text-slate-200 text-sm font-bold group/btn hover:bg-white hover:text-slate-900 transition-all duration-300"
                  >
                    <span>View Details</span>
                    <span className="material-symbols-outlined text-xl transform group-hover/btn:translate-x-1 transition-transform">arrow_forward</span>
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* No Results */}
          {!loading && !error && jobs?.length === 0 && (
            <div className="text-center py-12">
              <span className="material-symbols-outlined text-6xl text-slate-600 mb-4">search_off</span>
              <h3 className="text-xl font-bold text-white mb-2">No jobs found</h3>
              <p className="text-slate-400">Try adjusting your filters or search query</p>
            </div>
          )}

          {/* Load More Button */}
          {!loading && !error && jobs?.length > 0 && (
            <div className="mt-20 text-center">
              <button className="px-10 py-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl text-slate-200 font-bold transition-all hover:scale-105 active:scale-95 shadow-xl">
                Load More Opportunities
              </button>
            </div>
          )}
        </section>
      </main>

      {/* Footer */}
      <footer className="py-16 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center gap-8">
          <Link href="/homepage" className="flex items-center gap-3">
            <div className="relative w-8 h-8 rounded-lg overflow-hidden">
              <Image
                src="/assets/images/C_-_GoPMM_2_-_Square-1770356625482.png"
                alt="GoPMM Community Logo"
                fill
                className="object-contain"
              />
            </div>
            <span className="font-display font-bold text-xl">GoPMM</span>
          </Link>
          <div className="flex flex-wrap justify-center gap-8 text-sm font-semibold text-slate-500">
            <a className="hover:text-[#FF6B4A] transition-colors" href="https://www.linkedin.com/company/gopmm/">LinkedIn</a>
            <a className="hover:text-[#FF6B4A] transition-colors" href="mailto:varun@gopmm.in">Contact Us</a>
          </div>
          <p className="text-sm text-slate-500">© 2024 GoPMM Community. Built for India.</p>
        </div>
      </footer>
    </div>
  );
}